// ui/glossaryModal.js
// Glossary modal UI - help system with game term definitions

import { skillRegistry } from '../core/skillRegistry.js';
import { SKILLS } from '../game/constants.js';

let modalElement = null;
let searchFilter = '';
let collapsedCategories = new Set();
let searchDebounceTimer = null;
let showSpoilers = false;
let revealedSpoilers = new Set();

/**
 * Glossary terms organized by category
 * Terms can have inline spoilers using spoilerText field
 * The 'full' field is always shown, 'spoilerText' is hidden until revealed
 */
const GLOSSARY_CATEGORIES = {
    SKILLS: {
        id: 'skills',
        name: 'Skills',
        icon: '📊',
        terms: [
            {
                id: 'skill_mining',
                name: 'Mining',
                short: 'Extract ores from rocks',
                full: 'Mine rocks to gather ores. Ores are processed into bars via Smithing. Higher levels unlock better ores and faster mining.',
                usedIn: ['Smithing']
            },
            {
                id: 'skill_smithing',
                name: 'Smithing',
                short: 'Smelt ores into bars',
                full: 'Process ores from Mining into metal bars. Bars are used in Crafting for armor and in Woodworking for arrowheads.',
                usedIn: ['Crafting', 'Woodworking']
            },
            {
                id: 'skill_woodcutting',
                name: 'Woodcutting',
                short: 'Chop trees for logs',
                full: 'Cut down trees to gather logs. Logs are used in Pyre, Woodworking, and Architecting.',
                usedIn: ['Pyre', 'Woodworking', 'Architecting']
            },
            {
                id: 'skill_pyre',
                name: 'Pyre',
                short: 'Burn logs for perception',
                full: 'Burn logs to produce ashes and increase your perception.',
                spoilerText: 'Higher Pyre levels reveal hidden depths in combat zones with stronger enemies and better rewards.'
            },
            {
                id: 'skill_woodworking',
                name: 'Woodworking',
                short: 'Craft bows and arrows',
                full: 'Process logs into arrow shafts, craft bows, and combine shafts with arrowheads to make arrows for ranged combat.',
                usedIn: ['Combat (ranged)']
            },
            {
                id: 'skill_architecting',
                name: 'Architecting',
                short: 'Unlock milestones',
                full: 'Build structures to unlock milestones that expand the game: more bank tabs, farm plots, chest slots, and more.',
                spoilerText: 'Also unlocks pet quarters for Husbandry and guild expansions for Pilfering. Completing milestones guarantees a civic contract will appear (requires level 30+).'
            },
            {
                id: 'skill_fishing',
                name: 'Fishing',
                short: 'Catch fish from water',
                full: 'Fish at various spots to catch raw fish. Fish are cooked via Cooking skill for food that heals in combat.',
                usedIn: ['Cooking']
            },
            {
                id: 'skill_cooking',
                name: 'Cooking',
                short: 'Prepare food for healing',
                full: 'Cook raw fish and other ingredients into food. Food heals HP during combat - essential for surviving tough zones.',
                usedIn: ['Combat']
            },
            {
                id: 'skill_farming',
                name: 'Farming',
                short: 'Grow crops and herbs',
                full: 'Plant seeds in farm plots and wait for growth. Herbs are used in Alchemy for potions. Trees provide logs passively.',
                spoilerText: 'Unlock up to 12 plots as you level. Auto-harvest unlocks at level 50.',
                usedIn: ['Alchemy', 'Woodcutting (logs)']
            },
            {
                id: 'skill_alchemy',
                name: 'Alchemy',
                short: 'Brew potions',
                full: 'Clean herbs from Farming, then brew them into potions. Potions provide temporary combat buffs and other effects.',
                usedIn: ['Combat']
            },
            {
                id: 'skill_pilfering',
                name: 'Pilfering',
                short: 'Pickpocket for gold',
                full: 'Pickpocket NPCs to steal gold directly. Higher levels unlock better targets with more gold.',
                spoilerText: 'At Pilfering 30 + Guild Hall milestone (Architecting 20), unlock the Pilfering Guild for passive material gathering.'
            },
            {
                id: 'skill_crafting',
                name: 'Crafting',
                short: 'Create armor and jewelry',
                full: 'Combine materials from various skills to craft armor sets and jewelry. Equipment provides combat stats.',
                usedIn: ['Combat']
            },
            {
                id: 'skill_glyphbinding',
                name: 'Glyphbinding',
                short: 'Harvest ichor, craft glyphs',
                full: 'Gather essence from magical vats and craft it into elemental glyphs. Glyphs are consumed when casting spells in magic combat.',
                spoilerText: 'Combine pairs of glyphs into Glyph Cores, which are used in Woodworking to craft magical staves.',
                usedIn: ['Combat (magic)', 'Woodworking']
            },
            {
                id: 'skill_gatherer',
                name: 'Gatherer',
                short: 'Forage rare materials',
                full: 'Explore gathering zones to collect pet food and rare awakening materials. Three tiers of zones unlock as you level.',
                spoilerText: 'The ONLY source of pet food (basic/refined/condensed) and pet awakening materials like twisted root fiber.',
                usedIn: ['Husbandry']
            },
            {
                id: 'skill_husbandry',
                name: 'Husbandry',
                short: 'Manage pets',
                full: 'Unlock and care for pets that complete contracts passively in the background. Feed them with food from Gatherer to increase their speed.',
                spoilerText: 'Awaken pets through 3 tiers for better speed bonuses. Each tier requires matching food (basic→refined→condensed). Loot is stored in the pet chest.'
            },
            {
                id: 'skill_bounty',
                name: 'Bounty',
                short: 'Complete contracts',
                full: 'Accept and complete contracts for bonus XP and gold. Contracts include kill counts, gathering quotas, and more.'
            },
            {
                id: 'skill_combat',
                name: 'Combat',
                short: 'Fight monsters',
                full: 'Enter combat zones to fight monsters. Gain Combat XP, gold, and item drops. Higher zones offer better rewards but tougher enemies.'
            }
        ]
    },
    CORE: {
        id: 'core',
        name: 'Core Mechanics',
        icon: '⚙️',
        terms: [
            {
                id: 'xp',
                name: 'Experience (XP)',
                short: 'Points earned to level up skills',
                full: 'Experience points are earned by performing skill actions. Accumulate enough XP to level up your skill, unlocking new content.'
            },
            {
                id: 'level',
                name: 'Level',
                short: 'Your proficiency in a skill (1-99)',
                full: 'Each skill has a level from 1 to 99. Higher levels unlock new resources, tools, and abilities.'
            },
            {
                id: 'virtual_level',
                name: 'Virtual Levels',
                short: 'Progress beyond 99',
                full: 'After reaching level 99, you can continue gaining XP.',
                spoilerText: 'Virtual levels go up to 120. Reaching 120 grants Mastery with a permanent 5% speed bonus for that skill.'
            },
            {
                id: 'tick',
                name: 'Game Tick',
                short: 'Base time unit (100ms)',
                full: 'The game updates every 100 milliseconds (10 times per second). All actions, progress, and timers are calculated based on ticks.'
            },
            {
                id: 'offline',
                name: 'Offline Progress',
                short: 'Continue earning while away',
                full: 'When you close the game, your active skill continues working for up to 24 hours. Progress is calculated when you return.'
            }
        ]
    },
    COMBAT: {
        id: 'combat',
        name: 'Combat',
        icon: '⚔️',
        terms: [
            {
                id: 'hp',
                name: 'Health Points (HP)',
                short: 'Your life total (max 99)',
                full: 'Health points represent your life. At 0 HP, you respawn. Max HP is always 99.'
            },
            {
                id: 'attack',
                name: 'Attack',
                short: 'Damage dealt per hit',
                full: 'Your attack stat determines how much damage you deal to enemies. Equipment and combat level affect your attack.'
            },
            {
                id: 'defense',
                name: 'Defense',
                short: 'Damage reduction from hits',
                full: 'Defense reduces incoming damage. Higher defense means enemies deal less damage to you per hit.'
            },
            {
                id: 'attack_speed',
                name: 'Attack Speed',
                short: 'Time between attacks',
                full: 'How quickly you attack enemies. Faster attack speed means more hits per minute, increasing damage output and XP rate.'
            },
            {
                id: 'accuracy',
                name: 'Accuracy',
                short: 'Chance to hit enemies',
                full: 'Your chance to successfully hit an enemy. Higher accuracy means fewer missed attacks.'
            },
            {
                id: 'auto_eat',
                name: 'Auto-Eat',
                short: 'Automatic food consumption',
                full: 'Automatically consumes food when HP drops below a threshold.',
                spoilerText: 'Unlocked by building the Mess Hall milestone (Architecting 5). Store food with Shift+Click in inventory. Set your HP threshold in Combat settings.'
            },
            {
                id: 'zone',
                name: 'Combat Zone',
                short: 'Area with specific mobs',
                full: 'Combat zones contain groups of related enemies. Each zone has recommended combat levels and specific drop tables.'
            },
            {
                id: 'hidden_depths',
                name: 'Hidden Depths',
                short: 'Secret zone areas',
                full: 'Some combat zones have hidden depths.',
                spoilerText: 'Revealed by high Pyre levels. Hidden depths contain stronger enemies with better drops and more XP.'
            }
        ]
    },
    RESOURCES: {
        id: 'resources',
        name: 'Resources & Items',
        icon: '📦',
        terms: [
            {
                id: 'gold',
                name: 'Gold',
                short: 'Primary currency',
                full: 'Gold is earned from combat, pilfering, and selling items. Used to buy tools, equipment, and unlock features.'
            },
            {
                id: 'inventory',
                name: 'Inventory',
                short: '28 slots for items',
                full: 'Your personal item storage with 28 slots. Items of the same type stack. Full inventory prevents picking up new items.'
            },
            {
                id: 'bank',
                name: 'Bank',
                short: 'Extended item storage',
                full: 'The bank provides additional storage with unlimited stacking. Bank slots and tabs are unlocked through Architecting milestones.'
            },
            {
                id: 'equipment',
                name: 'Equipment',
                short: 'Gear that provides stats',
                full: 'Weapons, armor, and accessories that boost your combat stats. Equipment is crafted via Crafting or dropped from monsters.'
            },
            {
                id: 'tools',
                name: 'Tools',
                short: 'Skill efficiency upgrades',
                full: 'Each gathering skill has tools (pickaxes, axes, rods, etc.) that increase action speed. Better tools are bought with gold at higher levels.'
            }
        ]
    },
    SYSTEMS: {
        id: 'systems',
        name: 'Game Systems',
        icon: '🔧',
        terms: [
            {
                id: 'corruption',
                name: 'Corruption',
                short: 'Risk/reward modifier',
                full: 'A system that modifies combat difficulty and rewards.',
                spoilerText: 'Corruption increases XP, loot, and monster strength. Higher corruption = higher rewards and danger. Gained from certain actions.'
            },
            {
                id: 'bounty_system',
                name: 'Bounty Contracts',
                short: 'Repeatable task system',
                full: 'Accept contracts to complete specific goals (kill counts, gathering, crafting). Completing contracts awards Bounty XP and gold rewards.'
            },
            {
                id: 'pilfering_guild',
                name: 'Pilfering Guild',
                short: 'Passive gathering network',
                full: 'A network of pilferers that gather materials for you while you do other activities.',
                spoilerText: 'Requires Pilfering 30 + Guild Hall (Architecting 20) + 40k gold. Unlock up to 7 pilferer slots. Expand chest via Architecting milestones.'
            },
            {
                id: 'pet_system',
                name: 'Pet System',
                short: 'Passive contract runners',
                full: 'Unlock 5 pets through Husbandry levels (1, 15, 30, 50, 75). Each pet specializes in different contract types.',
                spoilerText: 'Feed pets with tier-matched food from Gatherer. Awaken through 3 stages for speed bonuses. Rewards go to the loot chest (expandable via Architecting).'
            },
            {
                id: 'outerrealms',
                name: 'Outerrealms',
                short: 'Mysterious dimension',
                full: 'A strange realm beyond normal perception.',
                spoilerText: 'Accessible through very high Pyre levels. Contains unique challenges and rewards not found in the normal world.'
            }
        ]
    },
    BONUSES: {
        id: 'bonuses',
        name: 'Bonuses & Modifiers',
        icon: '📈',
        terms: [
            {
                id: 'speed_mult',
                name: 'Speed Multiplier',
                short: 'Action time reduction',
                full: 'Tools provide speed multipliers that reduce action times. A 1.5x multiplier means actions complete 50% faster.'
            },
            {
                id: 'xp_mult',
                name: 'XP Multiplier',
                short: 'Bonus experience rate',
                full: 'Modifiers that increase XP earned per action. Various sources can provide XP multipliers.'
            },
            {
                id: 'loot_mult',
                name: 'Loot Multiplier',
                short: 'Bonus item quantities',
                full: 'Modifiers that increase item drops from combat and gathering.',
                spoilerText: 'Corruption and Pyre bonuses can provide significant loot multipliers in combat zones.'
            },
            {
                id: 'pyre_bonus',
                name: 'Pyre Bonus',
                short: 'Combat zone modifier',
                full: 'Bonuses gained from having high Pyre level.',
                spoilerText: 'When your Pyre exceeds a zone requirement, you gain +damage, +speed, and +loot (up to +10% at Pyre 99).'
            },
            {
                id: 'pet_speed',
                name: 'Pet Speed Bonus',
                short: 'Faster contract completion',
                full: 'Pets have a speed bonus that affects contract completion time.',
                spoilerText: 'Feeding pets and evolving them increases their speed bonus significantly, making them complete contracts much faster.'
            }
        ]
    }
};

/**
 * Get all categories with search filtering applied
 */
function getFilteredCategories() {
    if (!searchFilter.trim()) {
        return Object.values(GLOSSARY_CATEGORIES);
    }

    const query = searchFilter.toLowerCase();
    const filtered = [];

    for (const category of Object.values(GLOSSARY_CATEGORIES)) {
        const matchingTerms = category.terms.filter(term =>
            term.name.toLowerCase().includes(query) ||
            term.short.toLowerCase().includes(query) ||
            term.full.toLowerCase().includes(query) ||
            (term.spoilerText && term.spoilerText.toLowerCase().includes(query)) ||
            (term.usedIn && term.usedIn.some(s => s.toLowerCase().includes(query)))
        );

        if (matchingTerms.length > 0) {
            filtered.push({
                ...category,
                terms: matchingTerms
            });
        }
    }

    return filtered;
}

/**
 * Render a single term entry
 * - 'full' text is always shown
 * - 'spoilerText' is rendered as a clickable inline spoiler
 * - 'usedIn' shows skill connections
 */
function renderTermEntry(term) {
    const hasSpoiler = !!term.spoilerText;
    const spoilerRevealed = showSpoilers || revealedSpoilers.has(term.id);
    const spoilerClass = spoilerRevealed ? 'revealed' : '';

    // Build the spoiler text element if present
    let spoilerHtml = '';
    if (hasSpoiler) {
        const onClick = !spoilerRevealed ? `onclick="window.glossaryModal.revealSpoiler('${term.id}')"` : '';
        spoilerHtml = `
            <span class="spoiler ${spoilerClass}" ${onClick}>${term.spoilerText}</span>
        `;
    }

    // Build usedIn element if present
    let usedInHtml = '';
    if (term.usedIn && term.usedIn.length > 0) {
        usedInHtml = `
            <div class="glossary-term-usedin">
                <span class="usedin-label">Used in:</span> ${term.usedIn.join(', ')}
            </div>
        `;
    }

    return `
        <div class="glossary-term" data-term-id="${term.id}">
            <div class="glossary-term-header">
                <span class="term-name">${term.name}</span>
                ${hasSpoiler ? '<span class="term-spoiler-badge">🔒</span>' : ''}
            </div>
            <div class="glossary-term-short">${term.short}</div>
            <div class="glossary-term-full">${term.full} ${spoilerHtml}</div>
            ${usedInHtml}
        </div>
    `;
}

/**
 * Render a category section
 */
function renderCategorySection(category) {
    const isCollapsed = collapsedCategories.has(category.id);
    const collapsedClass = isCollapsed ? 'collapsed' : '';
    const toggleIcon = isCollapsed ? '▶' : '▼';

    const termsHtml = isCollapsed
        ? ''
        : `<div class="glossary-terms">
            ${category.terms.map(term => renderTermEntry(term)).join('')}
           </div>`;

    return `
        <div class="glossary-category ${collapsedClass}">
            <div class="glossary-category-header" onclick="window.glossaryModal.toggleCategory('${category.id}')">
                <span class="category-toggle">${toggleIcon}</span>
                <span class="category-icon">${category.icon}</span>
                <span class="category-name">${category.name}</span>
                <span class="category-count">${category.terms.length} terms</span>
            </div>
            ${termsHtml}
        </div>
    `;
}

/**
 * Render the body content
 */
function renderBodyContent() {
    const categories = getFilteredCategories();

    if (categories.length === 0) {
        return '<div class="glossary-no-results">No terms found matching your search.</div>';
    }

    return `
        <div class="glossary-categories">
            ${categories.map(cat => renderCategorySection(cat)).join('')}
        </div>
    `;
}

/**
 * Render the full modal content
 */
function renderModalContent() {
    const spoilerToggleClass = showSpoilers ? 'active' : '';
    const spoilerToggleIcon = showSpoilers ? '👁️' : '🔒';
    const spoilerToggleText = showSpoilers ? 'Hide Spoilers' : 'Show Spoilers';
    const spoilersVisibleClass = showSpoilers ? 'glossary-spoilers-visible' : '';

    return `
        <div class="modal-overlay" onclick="window.glossaryModal.handleOverlayClick(event)">
            <div class="modal-content glossary-modal ${spoilersVisibleClass}">
                <div class="modal-header">
                    <h2>📖 Glossary</h2>
                    <div class="glossary-header-controls">
                        <button class="spoiler-toggle ${spoilerToggleClass}" onclick="window.glossaryModal.toggleSpoilers()">
                            <span class="spoiler-toggle-icon">${spoilerToggleIcon}</span>
                            <span>${spoilerToggleText}</span>
                        </button>
                        <button class="modal-close" onclick="window.glossaryModal.close()">&times;</button>
                    </div>
                </div>
                <div class="modal-body">
                    <p class="glossary-intro">Quick reference for game terms and mechanics. <span class="glossary-spoiler-hint">🔒 = contains spoilers</span></p>
                    <div class="glossary-search-container">
                        <input type="text"
                               class="glossary-search"
                               placeholder="Search terms..."
                               value="${searchFilter}"
                               oninput="window.glossaryModal.handleSearch(this.value)">
                        ${searchFilter ? '<button class="glossary-search-clear" onclick="window.glossaryModal.clearSearch()">×</button>' : ''}
                    </div>
                    <div class="glossary-content-area">
                        ${renderBodyContent()}
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Open the Glossary modal
 */
export function openGlossaryModal() {
    searchFilter = '';
    collapsedCategories.clear();
    revealedSpoilers.clear(); // Reset individually revealed spoilers

    modalElement = document.createElement('div');
    modalElement.id = 'glossary-modal-container';
    modalElement.innerHTML = renderModalContent();
    document.body.appendChild(modalElement);

    // Focus search input
    const searchInput = modalElement.querySelector('.glossary-search');
    if (searchInput) {
        searchInput.focus();
    }

    // Add keyboard listener
    document.addEventListener('keydown', handleKeyDown);
}

/**
 * Close the Glossary modal
 */
export function closeGlossaryModal() {
    if (modalElement) {
        modalElement.remove();
        modalElement = null;
    }
    document.removeEventListener('keydown', handleKeyDown);
}

/**
 * Update only the content area
 */
function updateModalContent() {
    if (!modalElement) return;

    const contentArea = modalElement.querySelector('.glossary-content-area');
    if (contentArea) {
        const scrollContainer = modalElement.querySelector('.modal-body');
        const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

        contentArea.innerHTML = renderBodyContent();

        if (scrollContainer) {
            scrollContainer.scrollTop = scrollTop;
        }
    }
}

/**
 * Full modal update
 */
function updateModal() {
    if (!modalElement) return;

    const scrollContainer = modalElement.querySelector('.modal-body');
    const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

    modalElement.innerHTML = renderModalContent();

    const newScrollContainer = modalElement.querySelector('.modal-body');
    if (newScrollContainer) {
        newScrollContainer.scrollTop = scrollTop;
    }

    const searchInput = modalElement.querySelector('.glossary-search');
    if (searchInput && searchFilter.length > 0) {
        searchInput.focus();
        searchInput.setSelectionRange(searchFilter.length, searchFilter.length);
    }
}

/**
 * Handle search input with debouncing
 */
function handleSearch(value) {
    searchFilter = value;

    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
        updateModalContent();
    }, 100);
}

/**
 * Clear search
 */
function clearSearch() {
    searchFilter = '';
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }
    updateModal();
    const searchInput = modalElement?.querySelector('.glossary-search');
    if (searchInput) {
        searchInput.focus();
    }
}

/**
 * Toggle category collapse state
 */
function toggleCategory(categoryId) {
    if (collapsedCategories.has(categoryId)) {
        collapsedCategories.delete(categoryId);
    } else {
        collapsedCategories.add(categoryId);
    }
    updateModalContent();
}

/**
 * Handle overlay click (close on background click)
 */
function handleOverlayClick(event) {
    if (event.target.classList.contains('modal-overlay')) {
        closeGlossaryModal();
    }
}

/**
 * Handle keyboard events
 */
function handleKeyDown(event) {
    if (event.key === 'Escape') {
        closeGlossaryModal();
    }
}

/**
 * Toggle all spoilers visible/hidden
 */
function toggleSpoilers() {
    showSpoilers = !showSpoilers;
    updateModal();
}

/**
 * Reveal a single spoiler term
 */
function revealSpoiler(termId) {
    revealedSpoilers.add(termId);
    updateModalContent();
}

// Expose functions to window for onclick handlers
window.glossaryModal = {
    open: openGlossaryModal,
    close: closeGlossaryModal,
    handleSearch,
    clearSearch,
    toggleCategory,
    handleOverlayClick,
    toggleSpoilers,
    revealSpoiler
};

export default {
    openGlossaryModal,
    closeGlossaryModal
};
